import { Button } from '@/components/ui/button';

const PAGINATION_VISIBLE_PAGES = 5;

function getVisiblePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number = PAGINATION_VISIBLE_PAGES
): (number | null)[] {
  if (totalPages <= 0) return [];
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages: (number | null)[] = [];
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push(null);
  }
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(null);
    pages.push(totalPages);
  }
  return pages;
}

export interface DynamicTablePaginationProps {
  safePage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  onPrev: () => void;
  onNext: () => void;
  onPageChange: (page: number) => void;
}

export function DynamicTablePagination({
  safePage,
  pageSize,
  totalPages,
  totalRecords,
  onPrev,
  onNext,
  onPageChange,
}: DynamicTablePaginationProps) {
  return (
    <div className="p-4 border-t border-border flex flex-wrap items-center justify-between gap-3 bg-muted/20">
      <span className="text-sm text-muted-foreground">
        Showing {totalRecords === 0 ? 0 : (safePage - 1) * pageSize + 1}–
        {totalRecords === 0 ? 0 : Math.min(safePage * pageSize, totalRecords)} of {totalRecords}
      </span>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={onPrev}>
          Previous
        </Button>
        <nav className="flex items-center gap-1" aria-label="Pagination">
          {getVisiblePageNumbers(safePage, totalPages).map((page, idx) =>
            page === null ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-2 text-muted-foreground text-sm"
                aria-hidden
              >
                …
              </span>
            ) : (
              <Button
                key={page}
                type="button"
                variant={page === safePage ? 'secondary' : 'outline'}
                size="sm"
                className="min-w-9 h-9 p-0"
                onClick={() => onPageChange(page)}
                aria-current={page === safePage ? 'page' : undefined}
                aria-label={page === safePage ? `Page ${page}` : `Go to page ${page}`}
              >
                {page}
              </Button>
            )
          )}
        </nav>
        <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
